import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Loader2, Sparkles, Trash2, X, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  reconciliationApi,
  clientsApi,
  ClientData,
  ReconciliationFilters,
  MatchedPairData,
  JournalLine,
  BulkCommitRequest,
  TransactionFilters,
  mirageReconcileApi
} from "../lib/api";
import { generateReconciliationPairs } from "../lib/reconciliation";
import { UploadBankStatement } from "./UploadBankStatement";
import { MatchedPairCard } from "./MatchedPairCard";
import { ItemCard } from "./ui/item-card";
import { useToast } from "../hooks/use-toast";
import { ClientDropdown } from "./ui/client-dropdown";

export const InvoiceMatching: React.FC = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize selectedClient from URL query parameter
  const [selectedClient, setSelectedClient] = useState<string>(() => {
    return searchParams.get('client') || '';
  });

  // State management
  const [currentTab, setCurrentTab] = useState<'not-matched' | 'matched' | 'committed'>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'matched' || tabParam === 'committed' || tabParam === 'not-matched') {
      return tabParam;
    }
    return 'not-matched';
  });
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Data state
  // Cache of all matched pairs (matched + committed) for current client
  const [allPairs, setAllPairs] = useState<MatchedPairData[]>([]);
  const [unmatchedTransactions, setUnmatchedTransactions] = useState<any[]>([]);
  const [unmatchedInvoices, setUnmatchedInvoices] = useState<any[]>([]);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());

  const isCommittedView = currentTab === 'committed';

  // Derived, search-filtered views (client-side filtering; no refetch on tab switch)
  const normalizedQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);
  const filterText = (text?: string | number | null) => (text ?? '').toString().toLowerCase();

  const filteredUnmatchedTransactions = useMemo(() => {
    if (!normalizedQuery) return unmatchedTransactions;
    return unmatchedTransactions.filter((t: any) =>
      [t.reference, t.description, t.counterparty_name, t.client_name, t.amount, t.booking_ts]
        .some(v => filterText(v).includes(normalizedQuery))
    );
  }, [unmatchedTransactions, normalizedQuery]);

  const filteredUnmatchedInvoices = useMemo(() => {
    if (!normalizedQuery) return unmatchedInvoices;
    return unmatchedInvoices.filter((i: any) =>
      [i.filename || i.document_name, i.invoice_number, i.vendor?.name || i.vendor, i.customer?.name || i.customer, i.total_amount, i.invoice_date]
        .some(v => filterText(v).includes(normalizedQuery))
    );
  }, [unmatchedInvoices, normalizedQuery]);

  const matchedPairs = useMemo(() => allPairs.filter(p => !p.is_committed), [allPairs]);
  const committedPairs = useMemo(() => allPairs.filter(p => !!p.is_committed), [allPairs]);

  const filteredMatchedPairs = useMemo(() => {
    if (!normalizedQuery) return matchedPairs;
    return matchedPairs.filter(p =>
      [
        p.transaction.reference,
        p.transaction.purpose,
        p.transaction.from,
        p.transaction.to,
        p.transaction.amount,
        p.invoice.filename,
        p.invoice.invoice_number,
        p.invoice.vendor,
        p.invoice.customer
      ].some(v => filterText(v).includes(normalizedQuery))
    );
  }, [matchedPairs, normalizedQuery]);

  const filteredCommittedPairs = useMemo(() => {
    if (!normalizedQuery) return committedPairs;
    return committedPairs.filter(p =>
      [
        p.transaction.reference,
        p.transaction.purpose,
        p.transaction.from,
        p.transaction.to,
        p.transaction.amount,
        p.invoice.filename,
        p.invoice.invoice_number,
        p.invoice.vendor,
        p.invoice.customer,
        p.mirage_accrec_id
      ].some(v => filterText(v).includes(normalizedQuery))
    );
  }, [committedPairs, normalizedQuery]);

  // Selection state for not-matched view
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [selectedInvoicePercentage, setSelectedInvoicePercentage] = useState<number>(100);

  // Delete mode state
  const [deleteMode, setDeleteMode] = useState<boolean>(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [deletingTransactions, setDeletingTransactions] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState<boolean>(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingAutoMatch, setLoadingAutoMatch] = useState(false);
  const [loadingBulkCommit, setLoadingBulkCommit] = useState(false);
  const [unmatchingMatches, setUnmatchingMatches] = useState<Set<string>>(new Set());
  const [committingMatches, setCommittingMatches] = useState<Set<string>>(new Set());

  // API functions


  // Removed unused fetchUnmatchedData (logic is covered by fetchAllForClient)

  const fetchAllForClient = async () => {
    if (!selectedClient) {
      setAllPairs([]);
      setUnmatchedTransactions([]);
      setUnmatchedInvoices([]);
      return;
    }

    setLoading(true);
    try {
      const [transactionsResponse, invoicesResponse, pairsResponse] = await Promise.all([
        reconciliationApi.getTransactions({
          match_status: 'unmatched',
          client_ids: [selectedClient],
          skip: 0,
          limit: 100
        }),
        // Fetch all invoices so we can surface partially matched ones
        reconciliationApi.getInvoices({
          match_status: 'all',
          client_ids: [selectedClient],
          skip: 0,
          limit: 100
        }),
        reconciliationApi.getMatchedPairsWithPreview({
          client_ids: [selectedClient],
          match_status: 'all',
          skip: 0,
          limit: 100
        } as ReconciliationFilters)
      ]);

      const unmatchedTrans = transactionsResponse.transactions.filter(
        (transaction: any) => !transaction.links || transaction.links.length === 0
      );

      // Compute remaining percentage per invoice (exclude CANCELLED and DISPUTED; include committed)
      const remainingPct = (invoice: any): number => {
        const links = Array.isArray(invoice.links) ? invoice.links : [];
        const sum = links
          .filter((l: any) => {
            const status = (l?.status || '').toString().toUpperCase();
            return status !== 'CANCELLED' && status !== 'DISPUTED';
          })
          .reduce((acc: number, l: any) => acc + (typeof l.match_percentage === 'number' ? l.match_percentage : 0), 0);
        const remaining = 100 - sum;
        return remaining > 0 ? remaining : 0;
      };

      // Not Matched invoices now include partials with remaining > 0
      const unmatchedInvs = invoicesResponse.invoices.filter(
        (invoice: any) => remainingPct(invoice) > 0
      ).map((invoice: any) => ({
        ...invoice,
        __remaining_percentage: remainingPct(invoice)
      }));

      setUnmatchedTransactions(unmatchedTrans);
      setUnmatchedInvoices(unmatchedInvs);
      setAllPairs(pairsResponse.matched_pairs);
    } catch (error) {
      toast({
        title: "Error fetching reconciliation data",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
      setUnmatchedTransactions([]);
      setUnmatchedInvoices([]);
      setAllPairs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsApi.getClients();
      setClients(response.clients);
    } catch (error) {
      toast({
        title: "Error fetching clients",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
      setClients([]);
    }
  };

  const handlePairSelection = (linkId: string, selected: boolean) => {
    const newSet = new Set(selectedPairs);
    if (selected) {
      newSet.add(linkId);
    } else {
      newSet.delete(linkId);
    }
    setSelectedPairs(newSet);
  };

  const handleAutoMatch = async () => {
    if (currentTab !== 'not-matched') {
      toast({
        title: "Switch to Not Matched",
        description: "Automatic matching only runs on unmatched items.",
        variant: "destructive"
      });
      return;
    }

    if (!selectedClient) {
      toast({
        title: "Select a client",
        description: "Choose a client before running automatic matching.",
        variant: "destructive"
      });
      return;
    }

    setLoadingAutoMatch(true);
    try {
      const filters: TransactionFilters = {
        match_status: "unmatched",
        client_ids: [selectedClient],
        limit: 100
      };

      const response = await reconciliationApi.autoMatch(filters);

      const warnings = response.warnings?.filter(Boolean) || [];
      const skipped = response.skipped?.length ?? 0;

      toast({
        title: response.created > 0 ? "Automatic matching completed" : "No automatic matches created",
        description: `${response.created} match${response.created === 1 ? "" : "es"} created automatically${skipped ? `. Skipped ${skipped} suggestion${skipped === 1 ? "" : "s"}.` : "."}${warnings.length ? ` ${warnings.join(" ")}` : ""}`,
        variant: response.created > 0 ? "default" : "destructive"
      });

      setSelectedTransaction(null);
      setSelectedInvoice(null);
      setSelectedInvoicePercentage(100);
      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Automatic matching failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setLoadingAutoMatch(false);
    }
  };

  const handleBulkCommit = async () => {
    if (selectedPairs.size === 0) return;

    setLoadingBulkCommit(true);
    try {
      const request: BulkCommitRequest = {
        link_ids: Array.from(selectedPairs)
      };

      const response = await reconciliationApi.bulkCommit(request);

      if (response.successful_commits.length > 0) {
        toast({
          title: "Bulk commit completed",
          description: `${response.successful_commits.length} matches committed successfully.`,
        });

        // Remove committed pairs from view and selection
        setSelectedPairs(new Set());
        await fetchAllForClient();
      }

      if (response.failed_commits.length > 0) {
        toast({
          title: "Some commits failed",
          description: `${response.failed_commits.length} matches failed to commit.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Bulk commit failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoadingBulkCommit(false);
    }
  };

  const handleCommit = async (
    pair: MatchedPairData,
    selection?: { debitLines: JournalLine[]; creditLines: JournalLine[] }
  ) => {
    const linkId = pair.link_id;
    if (committingMatches.has(linkId)) {
      return;
    }

    const selectedDebitLines = selection?.debitLines?.length
      ? selection.debitLines
      : pair.preview.debit_lines;
    const selectedCreditLines = selection?.creditLines?.length
      ? selection.creditLines
      : pair.preview.credit_lines;

    if (!selectedDebitLines.length || !selectedCreditLines.length) {
      toast({
        title: "Commit failed",
        description: "Account information is incomplete for this match.",
        variant: "destructive",
      });
      return;
    }

    setCommittingMatches(prev => {
      const next = new Set(prev);
      next.add(linkId);
      return next;
    });

    try {
      const recLines = generateReconciliationPairs(selectedDebitLines, selectedCreditLines);

      await reconciliationApi.updateLinkAccounts(linkId, {
        debit_lines: selectedDebitLines,
        credit_lines: selectedCreditLines,
        reconciliation_lines: recLines
      });

      const response = await mirageReconcileApi.confirm({
        link_id: linkId,
      });

      if (response.status !== "success") {
        throw new Error(response.error || response.message || "Accounting software commit failed.");
      }

      toast({
        title: "Committed to accounting software",
        description: response.message || "Match committed successfully.",
      });

      setSelectedPairs(prev => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });

      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Commit failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setCommittingMatches(prev => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });
    }
  };

  const handleUnmatch = async (linkId: string) => {
    setUnmatchingMatches(prev => new Set(prev).add(linkId));

    try {
      await reconciliationApi.unmatchPair(linkId);

      toast({
        title: "Match removed",
        description: "The invoice and transaction have been unmatched.",
      });

      // Remove from selected pairs and refresh data
      setSelectedPairs(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Failed to unmatch",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setUnmatchingMatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
    }
  };

  const isUnmatching = (linkId: string) => unmatchingMatches.has(linkId);
  const isCommittingMatch = (linkId: string) => committingMatches.has(linkId);

  const handleUploadComplete = () => {
    if (selectedClient) {
      fetchAllForClient();
    }
  };

  // Selection handlers for not-matched view
  const handleTransactionSelect = (transactionId: string, selected: boolean) => {
    if (selected) {
      setSelectedTransaction(transactionId);
    } else {
      setSelectedTransaction(null);
    }
  };

  const handleInvoiceSelect = (invoiceId: string, selected: boolean, percentage?: number) => {
    if (selected) {
      setSelectedInvoice(invoiceId);
      if (typeof percentage === 'number') {
        setSelectedInvoicePercentage(percentage);
      } else {
        setSelectedInvoicePercentage(100);
      }
    } else {
      setSelectedInvoice(null);
      setSelectedInvoicePercentage(100);
    }
  };

  const handleCreateMatch = async (transactionId: string, invoiceId: string, percentage?: number) => {
    try {
      // Create match via API
      await reconciliationApi.createMatch({
        transaction_id: transactionId,
        invoice_id: invoiceId,
        match_percentage: typeof percentage === 'number' ? percentage : selectedInvoicePercentage
      });

      toast({
        title: "Match created",
        description: "Transaction and invoice have been matched successfully.",
      });

      // Clear selections
      setSelectedTransaction(null);
      setSelectedInvoice(null);
      setSelectedInvoicePercentage(100);

      // Refresh data
      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Failed to create match",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  // Delete mode handlers
  const handleDeleteTransaction = async (transactionId: string) => {
    setDeletingTransactions(prev => new Set(prev).add(transactionId));

    try {
      await reconciliationApi.deleteTransaction(transactionId);

      toast({
        title: "Transaction deleted",
        description: "The transaction has been permanently removed.",
      });

      // Refresh data
      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Failed to delete transaction",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setDeletingTransactions(prev => {
        const newSet = new Set(prev);
        newSet.delete(transactionId);
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;

    const idsToDelete = Array.from(selectedForDelete);

    // Mark all as deleting
    setDeletingTransactions(new Set(idsToDelete));

    try {
      const response = await reconciliationApi.deleteTransactionsBulk({
        transaction_ids: idsToDelete
      });

      if (response.deleted_count > 0) {
        toast({
          title: "Transactions deleted",
          description: `${response.deleted_count} transaction(s) permanently removed.`,
        });
      }

      if (response.failed_ids.length > 0) {
        toast({
          title: "Some deletions failed",
          description: `${response.failed_ids.length} transaction(s) could not be deleted.`,
          variant: "destructive",
        });
      }

      // Clear selections and exit delete mode
      setSelectedForDelete(new Set());
      setDeleteMode(false);
      setShowBulkDeleteConfirm(false);

      // Refresh data
      await fetchAllForClient();
    } catch (error) {
      toast({
        title: "Bulk delete failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setDeletingTransactions(new Set());
    }
  };

  const handleDeleteSelection = (transactionId: string, selected: boolean) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(transactionId);
      } else {
        newSet.delete(transactionId);
      }
      return newSet;
    });
  };

  const exitDeleteMode = () => {
    setDeleteMode(false);
    setSelectedForDelete(new Set());
  };

  const handleSelectAllForDelete = () => {
    const allIds = filteredUnmatchedTransactions.map((t: any) => t.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedForDelete.has(id));

    if (allSelected) {
      // Deselect all
      setSelectedForDelete(new Set());
    } else {
      // Select all
      setSelectedForDelete(new Set(allIds));
    }
  };

  const allTransactionsSelectedForDelete = useMemo(() => {
    const allIds = filteredUnmatchedTransactions.map((t: any) => t.id);
    return allIds.length > 0 && allIds.every(id => selectedForDelete.has(id));
  }, [filteredUnmatchedTransactions, selectedForDelete]);

  // Sync selectedClient and currentTab with URL query parameters
  useEffect(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      // Sync client parameter
      if (selectedClient) {
        newParams.set('client', selectedClient);
      } else {
        newParams.delete('client');
      }

      // Sync tab parameter
      newParams.set('tab', currentTab);

      return newParams;
    });
  }, [selectedClient, currentTab, setSearchParams]);

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Preload and cache all data when client changes
  useEffect(() => {
    if (selectedClient) {
      fetchAllForClient();
    } else {
      setAllPairs([]);
      setUnmatchedTransactions([]);
      setUnmatchedInvoices([]);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (currentTab !== 'matched') {
      setSelectedPairs(new Set());
    }
  }, [currentTab]);

  // Reset delete mode when switching away from not-matched tab
  useEffect(() => {
    if (currentTab !== 'not-matched') {
      exitDeleteMode();
    }
  }, [currentTab]);

  return (
    <div className="w-full min-h-screen bg-white">
      {/* Upload Section with Header Outside */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-2 page-main-heading">Upload Bank Statement</h1>
        <UploadBankStatement
          clients={clients}
          selectedClient={selectedClient}
          onClientChange={setSelectedClient}
          onUploadComplete={handleUploadComplete}
        />
        <div className="mt-2 text-right">
          <Link
            to="/reconciliation/files"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
          >
            <FileText className="h-3 w-3" />
            View processed files
          </Link>
        </div>
      </div>

      {/* Main Matching Section */}
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Matching</h1>

        {/* Client Selection and Bulk Commit */}
        <div className="flex items-center justify-between bg-gray-100 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Client</span>
            <ClientDropdown
              value={selectedClient}
              onValueChange={setSelectedClient}
              placeholder="Select Client"
              multiple={false}
            />
          </div>

          <div className="flex items-center gap-3">
            {currentTab === 'not-matched' && !deleteMode && (
              <>
                <Button
                  variant="outline"
                  onClick={handleAutoMatch}
                  disabled={loadingAutoMatch || !selectedClient}
                  className="flex items-center gap-2"
                >
                  {loadingAutoMatch ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Auto Match
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteMode(true)}
                  disabled={!selectedClient || filteredUnmatchedTransactions.length === 0}
                  className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                  Select for Delete
                </Button>
              </>
            )}
            {currentTab === 'not-matched' && deleteMode && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={exitDeleteMode}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSelectAllForDelete}
                  disabled={filteredUnmatchedTransactions.length === 0}
                  className="flex items-center gap-2"
                >
                  {allTransactionsSelectedForDelete ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  disabled={selectedForDelete.size === 0 || deletingTransactions.size > 0}
                  className="flex items-center gap-2"
                >
                  {deletingTransactions.size > 0 ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {selectedForDelete.size > 0
                        ? `Delete ${selectedForDelete.size} Transaction(s)`
                        : 'Delete Selected'}
                    </>
                  )}
                </Button>
              </div>
            )}
            {currentTab === 'matched' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    const allIds = filteredMatchedPairs.map(p => p.link_id);
                    const allSelected = allIds.length > 0 && allIds.every(id => selectedPairs.has(id));
                    if (allSelected) {
                      setSelectedPairs(new Set());
                    } else {
                      setSelectedPairs(new Set(allIds));
                    }
                  }}
                  disabled={filteredMatchedPairs.length === 0}
                  className="flex items-center gap-2"
                >
                  {filteredMatchedPairs.length > 0 && filteredMatchedPairs.every(p => selectedPairs.has(p.link_id))
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
                <Button
                  onClick={handleBulkCommit}
                  disabled={loadingBulkCommit || selectedPairs.size === 0}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loadingBulkCommit ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Committing...
                    </>
                  ) : (
                    selectedPairs.size > 0
                      ? `Commit ${selectedPairs.size} Matches to accounting software`
                      : 'Commit matches to accounting software'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs and Search */}
        <div className="flex items-center justify-between">
          <Tabs value={currentTab} onValueChange={(value) => setCurrentTab(value as any)} className="w-auto">
            <TabsList className="bg-gray-100">
              <TabsTrigger value="not-matched">Not Matched <div className="bg-red-500 text-xs ml-2 py-1 px-2 rounded-full text-white">{filteredUnmatchedTransactions.length + filteredUnmatchedInvoices.length}</div></TabsTrigger>
              <TabsTrigger value="matched">Matched <div className="bg-[#0000FF] text-xs ml-2 py-1 px-2 rounded-full text-white">{filteredMatchedPairs.length}</div></TabsTrigger>
              <TabsTrigger value="committed">Committed <div className="bg-[#0000FF] text-xs ml-2 py-1 px-2 rounded-full text-white">{filteredCommittedPairs.length}</div></TabsTrigger>
            </TabsList>
          </Tabs>

          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-80"
          />
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-2 gap-8">
          <h3 className="text-lg font-medium">
            Bank Transactions ({currentTab === 'not-matched' ? (filteredUnmatchedTransactions.length) : (currentTab === 'matched' ? filteredMatchedPairs.length : filteredCommittedPairs.length)})
          </h3>
          <h3 className="text-lg font-medium">
            Invoices ({currentTab === 'not-matched' ? (filteredUnmatchedInvoices.length) : (currentTab === 'matched' ? filteredMatchedPairs.length : filteredCommittedPairs.length)})
          </h3>
        </div>

        {/* Content Area */}
        <div className="min-h-96">
          {!selectedClient ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
              <p className="text-lg font-semibold mb-2">Select a client to view data</p>
              <p className="text-sm max-w-md">
                Please choose a client from the dropdown above. Matched pairs will appear once a client is selected.
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : currentTab === 'not-matched' ? (
            // Unmatched view - show two separate columns with ItemCard components
            filteredUnmatchedTransactions.length === 0 && filteredUnmatchedInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No unmatched items found for this client
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-8">
                {/* Unmatched Transactions */}
                <div className="space-y-3">
                  {filteredUnmatchedTransactions.map((transaction) => (
                    <ItemCard
                      key={transaction.id}
                      title={transaction.reference || transaction.id.substring(0, 16)}
                      isMatched={false}
                      type="transaction"
                      data={[
                        { label: 'From', value: transaction.counterparty_name || 'Unknown' },
                        { label: 'To', value: transaction.client_name || 'Unknown' },
                        { label: 'Date', value: transaction.booking_ts || 'N/A', format: 'date' },
                        { label: 'Amount', value: transaction.amount || 0, format: 'currency', currencyCode: transaction.currency || 'EUR' },
                        { label: 'Purpose', value: transaction.description || 'N/A' }
                      ]}
                      // Selection behavior changes based on delete mode
                      isSelected={deleteMode ? selectedForDelete.has(transaction.id) : selectedTransaction === transaction.id}
                      onSelect={(selected) => deleteMode
                        ? handleDeleteSelection(transaction.id, selected)
                        : handleTransactionSelect(transaction.id, selected)
                      }
                      // In delete mode, disable match functionality
                      oppositeSelected={deleteMode ? false : !!selectedInvoice}
                      otherSelected={deleteMode
                        ? false
                        : (!!selectedTransaction && selectedTransaction !== transaction.id)
                      }
                      onMatch={deleteMode ? undefined : (selectedInvoice ? () => handleCreateMatch(transaction.id, selectedInvoice) : undefined)}
                      // Delete functionality - show trash icon when NOT in delete mode
                      showDeleteButton={!deleteMode}
                      onDelete={() => handleDeleteTransaction(transaction.id)}
                      isDeleting={deletingTransactions.has(transaction.id)}
                    />
                  ))}
                </div>

                {/* Unmatched Invoices */}
                <div className="space-y-3">
                  {filteredUnmatchedInvoices.map((invoice) => (
                    <ItemCard
                      key={invoice.id}
                      title={invoice.filename || invoice.document_name || 'Invoice'}
                      isMatched={false}
                      type="invoice"
                      data={[
                        { label: 'Invoice Date', value: invoice.invoice_date || 'N/A', format: 'date' },
                        { label: 'Invoice Number', value: invoice.invoice_number || 'N/A' },
                        { label: 'Vendor', value: typeof invoice.vendor === 'object' ? invoice.vendor.name : invoice.vendor || 'N/A' },
                        { label: 'Customer', value: typeof invoice.customer === 'object' ? invoice.customer.name : invoice.customer || 'N/A' },
                        { label: 'Amount', value: invoice.total_amount || 0, format: 'currency', currencyCode: invoice.currency || 'EUR' }
                      ]}
                      isSelected={selectedInvoice === invoice.id}
                      onSelect={(selected, percentage) => handleInvoiceSelect(invoice.id, selected, percentage)}
                      oppositeSelected={!!selectedTransaction}
                      otherSelected={!!selectedInvoice && selectedInvoice !== invoice.id}
                      onMatch={selectedTransaction ? (percentage) => handleCreateMatch(selectedTransaction, invoice.id, percentage) : undefined}
                      allowPartialMatch={true}
                      // Suggest remaining percentage if partially matched (exact, no rounding rules applied here)
                      defaultPercentage={typeof invoice.__remaining_percentage === 'number' ? invoice.__remaining_percentage : 100}
                    />
                  ))}
                </div>
              </div>
            )
          ) : (currentTab === 'matched' ? filteredMatchedPairs.length === 0 : filteredCommittedPairs.length === 0) ? (
            <p className="text-gray-500 text-center py-8">
              {currentTab === 'committed' ? 'No committed matches found for this client' : 'No matched pairs found for this client'}
            </p>
          ) : (
            <div className="space-y-4">
              {(currentTab === 'matched' ? filteredMatchedPairs : filteredCommittedPairs).map((pair) => (
                <MatchedPairCard
                  key={pair.link_id}
                  pair={pair}
                  isSelected={!isCommittedView && selectedPairs.has(pair.link_id)}
                  isUnmatching={!isCommittedView && isUnmatching(pair.link_id)}
                  onSelect={(selected) => {
                    if (isCommittedView) return;
                    handlePairSelection(pair.link_id, selected);
                  }}
                  onUnmatch={() => handleUnmatch(pair.link_id)}
                  onCommit={!isCommittedView ? (selection) => handleCommit(pair, selection) : undefined}
                  isCommitting={!isCommittedView && isCommittingMatch(pair.link_id)}
                  readOnly={isCommittedView}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transactions</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete {selectedForDelete.size} transaction(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(false)}
              disabled={deletingTransactions.size > 0}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deletingTransactions.size > 0}
            >
              {deletingTransactions.size > 0 ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedForDelete.size} Transaction(s)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceMatching;
